'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useMemo, useState } from 'react';

import {
  fetchReservationServices,
  type ReservationService,
  type ReservationCategory,
} from '@eliadmin/core/src/api/reservation/reservation-api';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type GroupedServices = Array<{
  title: string;
  services: ReservationService[];
}>;

const VARIANT_ORDER = [
  'krátke',
  'stredné',
  'dlhé',
  'extra dlhé',
  'na sucho',
  'cez čiapku',
  '1 ampulka',
];

function splitServiceName(fullName: string): { base: string; variant: string } {
  const parts = fullName
    .split(' - ')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) return { base: fullName.trim(), variant: fullName.trim() };
  return { base: parts[0], variant: parts.slice(1).join(' - ') };
}

function variantRank(variant: string): number {
  const normalized = variant.toLowerCase();
  const idx = VARIANT_ORDER.findIndex(v => normalized === v);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

function groupServicesForCards(raw: ReservationService[]): GroupedServices {
  const map = new Map<string, ReservationService[]>();

  for (const s of raw) {
    const { base, variant } = splitServiceName(s.name);

    const serviceForCard: ReservationService = { ...s, name: variant };

    const arr = map.get(base) ?? [];
    arr.push(serviceForCard);
    map.set(base, arr);
  }

  const groups: GroupedServices = Array.from(map.entries()).map(([title, services]) => {
    const sorted = [...services].sort((a, b) => {
      const ra = variantRank(a.name);
      const rb = variantRank(b.name);
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name, 'sk');
    });

    return { title, services: sorted };
  });

  groups.sort((a, b) => a.title.localeCompare(b.title, 'sk'));
  return groups;
}

export function PriceList({ categories }: { categories: ReservationCategory[] }) {
  const [activeTab, setActiveTab] = useState<string>(categories[0] ? String(categories[0].id) : '');

  const activeTabId = activeTab ? Number(activeTab) : undefined;
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: servicesResponse } = useQuery({
    queryKey: ['RESERVATION_SERVICE', locale, activeTabId],
    queryFn: () => fetchReservationServices({ locale, query: { categoryId: activeTabId! } }),
    enabled: Boolean(activeTabId),
  });

  const services: ReservationService[] = servicesResponse?.data ?? [];

  const grouped = useMemo(() => groupServicesForCards(services), [services]);

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={setActiveTab}
      className={cn(
        'custom-container',
        'relative z-10 flex flex-col items-start justify-start gap-8 sm:gap-10 md:flex-row md:justify-between lg:gap-12',
      )}
    >
      <TabsList className="grid h-fit w-full grid-cols-3 gap-2 rounded-none bg-transparent p-0 shadow-none md:max-w-[486px] md:grid-cols-1">
        {categories.map(category => {
          const isActive = activeTab === String(category.id);

          return (
            <TabsTrigger
              key={category.id}
              value={String(category.id)}
              className={cn(
                'ea-price-list__tab-trigger',
                'col-span-1 flex w-full flex-col items-center justify-center gap-2 rounded-[24px] p-4 text-lg font-semibold leading-[1.1] tracking-tight shadow-none transition-all sm:flex-row sm:justify-start sm:leading-[1.1] md:gap-4 md:text-xl lg:p-6 lg:text-[22px] lg:leading-[1.1]',
                isActive
                  ? 'cursor-context-menu bg-black text-white hover:bg-black'
                  : 'cursor-pointer bg-white text-black hover:bg-white/60',
              )}
            >
              <Image
                src={category?.image?.url ?? ''}
                alt={resolveImageAlt(category?.image, category?.name)}
                width={0}
                height={0}
                className="max-h-12 min-h-12 min-w-12 max-w-12 rounded-[7px] object-cover object-center lg:max-h-14 lg:min-h-14 lg:min-w-14 lg:max-w-14 xl:max-h-16 xl:min-h-16 xl:min-w-16 xl:max-w-16"
                sizes="100vw"
              />
              {category.name}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <TabsContent
        value={activeTab}
        className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6"
      >
        {grouped.map(group => (
          <CategoriedCard key={group.title} title={group.title} services={group.services} />
        ))}
      </TabsContent>
    </Tabs>
  );
}

function CategoriedCard({ title, services }: { title: string; services: ReservationService[] }) {
  function formatEuro(value: string | number): string {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return `${value} €`;

    const hasCents = Math.round(n * 100) % 100 !== 0;
    return `${hasCents ? n.toFixed(2) : Math.trunc(n)} €`;
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[8px] border border-black/10 p-4 sm:p-5 lg:p-6">
      <h3 className="text-lg font-bold tracking-tighter sm:text-xl lg:text-[22px]">{title}</h3>

      <div className="flex w-full flex-col items-start justify-start gap-4">
        {services.map((service, index) => (
          <div
            key={`${title}-${service.id}`}
            className={cn(
              'flex w-full items-center justify-between border-b border-b-black/10 pb-4',
              {
                'last:border-b-0 last:pb-0': index === services.length - 1,
              },
            )}
          >
            <p className="text-sm font-semibold sm:text-base lg:text-lg">{service.name}</p>
            <p className="text-sm font-semibold sm:text-base lg:text-lg">
              {formatEuro(service.price_with_vat)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
